import { useState } from "react";

/** The types that ship with the platform. Organisers can add their own. */
export const BUILT_IN_EVENT_TYPES = [
  "workshop",
  "seminar",
  "networking",
  "career_fair",
  "mentoring",
  "webinar",
  "hackathon",
  "competition",
] as const;

/** "Pitching Session" → "pitching_session" */
export function toTypeSlug(label: string): string {
  return label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);
}

/** "pitching_session" → "Pitching Session" */
export function typeLabel(slug: string): string {
  if (!slug) return "";
  return slug
    .split("_")
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");
}

const REMEMBERED_KEY = 'up:customEventTypes';

/** Types this admin has created before, so they need typing only once. */
function readRemembered(): string[] {
  try {
    const raw = localStorage.getItem(REMEMBERED_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function remember(slug: string) {
  try {
    const next = Array.from(new Set([...readRemembered(), slug])).slice(-25);
    localStorage.setItem(REMEMBERED_KEY, JSON.stringify(next));
  } catch {
    // Private browsing, or storage disabled. Not worth failing over.
  }
}

interface EventTypePickerProps {
  value: string;
  onChange: (slug: string) => void;
  id?: string;
}

/**
 * Picks the event's type, with the option to define a new one.
 *
 * The chosen value is written to both `category` and `eventType` by the form
 * around this, because members see and filter by `eventType` while the admin
 * screens group by `category`. Keeping them in step stops an event created here
 * from showing as "other" on the public page.
 */
export default function EventTypePicker({
  value,
  onChange,
  id = "eventType",
}: EventTypePickerProps) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");
  const [remembered, setRemembered] = useState<string[]>(() => readRemembered());

  const known = Array.from(
    new Set([...BUILT_IN_EVENT_TYPES, ...remembered, value].filter(Boolean))
  );

  const commit = () => {
    const slug = toTypeSlug(draft);
    if (slug.length < 2) {
      setError("Give the type a name of at least two characters.");
      return;
    }
    remember(slug);
    setRemembered(readRemembered());
    onChange(slug);
    setAdding(false);
    setDraft("");
    setError("");
  };

  if (adding) {
    return (
      <div className="space-y-2">
        <div className="flex gap-2">
          <input
            id={id}
            type="text"
            value={draft}
            autoFocus
            onChange={(e) => { setDraft(e.target.value); setError(""); }}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); commit(); }
              if (e.key === "Escape") { setAdding(false); setDraft(""); setError(""); }
            }}
            placeholder="e.g. Expo, Pitching Session"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="button"
            onClick={commit}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Use it
          </button>
          <button
            type="button"
            onClick={() => { setAdding(false); setDraft(""); setError(""); }}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        {draft.trim() && !error && (
          <p className="text-xs text-gray-500">
            Members will see this as <strong>{typeLabel(toTypeSlug(draft))}</strong>
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <select
        id={id}
        value={value}
        onChange={(e) => {
          if (e.target.value === "__new__") { setAdding(true); return; }
          onChange(e.target.value);
        }}
        required
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        {known.map((slug) => (
          <option key={slug} value={slug}>{typeLabel(slug)}</option>
        ))}
        <option value="__new__">+ Add a new type…</option>
      </select>
      {value && !BUILT_IN_EVENT_TYPES.includes(value as any) && (
        <p className="text-xs text-gray-500">Your own type. Members will see “{typeLabel(value)}”.</p>
      )}
    </div>
  );
}
